import type { Actor } from './actor.ts';
import type { AppliedChange } from './characterChange.ts';
import type { InventoryEntry } from '../rules/inventory.ts';
import type { Hotlist } from '../rules/hotlist.ts';
import type { StatBlock } from './stats.ts';
import type { PetCompanion } from './pet.ts';

/** Current schema version of the stored character record. */
export const CHARACTER_SCHEMA_VERSION = 15;

export interface StatAdvancement {
  levelAccountedFor: number;
  unspentPoints: number;
  needsReview: boolean;
}


/** How many past changes a crawler carries. Old entries fall off the end. */
export const CHANGE_LOG_LIMIT = 50;

/**
 * Crawler type. Determines which Skills are legal.
 * Source: Crawlers & Customization p.6, p.10 (human vs animal crawlers).
 */
export type CrawlerType = 'human' | 'animal';

/**
 * A Skill the crawler actually has.
 * RULE DCC-SKILL-003 - only Skills at Rank 1 or higher are recorded.
 * Source: Crawlers & Customization p.6
 */
export interface CharacterSkill {
  /** Stable content ID, e.g. "perception". */
  skillId: string;
  rank: number;
}

/**
 * What the player chose during creation. Kept so a crawler's starting Skills
 * can be audited back to the printed tables, and so the creator can be
 * reopened later. Null for crawlers made before this was recorded.
 * Source: Crawlers & Customization pp.6-13.
 */
/** One item handed over at creation, by stable ID. */
export interface ItemGrant {
  itemId: string;
  quantity: number;
}

export interface CharacterOrigin {
  backgrounds: {
    tableId: string;
    backgroundId: string;
    skillIds: string[];
    /**
     * What THIS Background granted at creation, as Items.
     *
     * Recorded per Background rather than merged into `grants`, so the future
     * origin-editing slice (ROADMAP D1) can tell what one Background gave
     * without reconstructing it. It must not be inferred from current
     * inventory: potions get drunk and more get bought, so a quantity today
     * says nothing about what creation handed over.
     */
    grantedItems: ItemGrant[];
  }[];
  combat: {
    automaticSkillId: string | null;
    second: { kind: 'weapon' | 'spell' | 'hand-to-hand'; skillId: string } | null;
  };
  /**
   * Items granted at creation by the combat choice, e.g. 5 Standard Mana
   * Potions or a Bronze Weapon Box (C&C p.13).
   *
   * This is the HISTORICAL RECORD of what the crawler was handed, kept
   * alongside - not instead of - the ordinary inventory those grants became.
   * The two answer different questions: the inventory says what the crawler has
   * now, and this says what creation gave them. A crawler who drank the potions
   * still has the record; a crawler who bought more still has the record of
   * five.
   *
   * The future "edit creation record" slice (ROADMAP D1/D2) needs this to
   * unwind a changed combat choice correctly, which it could not do from a
   * quantity that has since moved.
   */
  grants: ItemGrant[];
  /**
   * Old creation-grant text that no Item could be matched to.
   *
   * Real schema, not an incidental extra property. Before Items existed,
   * `grants` was free text; the 6 -> 7 migration matches what it can and keeps
   * the rest HERE, verbatim. It is deliberately never interpreted as an Item
   * later - a string nobody could parse then will not become parseable by
   * being guessed at now.
   *
   * Normally empty. Typed, validated and carried through `set-origin`, undo and
   * export, because a promise to preserve something has to be kept by the
   * schema rather than by luck.
   */
  unmatchedGrants: string[];
}

export interface Character extends Actor {
  readonly kind: 'character';
  schemaVersion: number;
  crawlerType: CrawlerType;
  level: number;
  /** Earned Stat Points, independent of Race/Class and equipment bonuses. */
  statAdvancement?: StatAdvancement;
  /** GM-confirmed Race/Class exceptions to the ordinary Rank 15 ceiling. */
  skillRankLimits?: Record<string, 20>;
  floor: number;
  /**
   * Base Stat layer. The Enhanced layer is DERIVED, never stored.
   * RULE DCC-STAT-LAYER-001 - Rules for Survival p.11
   */
  unenhancedStats: StatBlock;
  skills: CharacterSkill[];
  /**
   * Health Bar slots not yet marked off. The slot VALUE and the maximum number
   * of slots are derived from Con (see rules/health.ts) and are never stored.
   */
  healthBarSlotsRemaining: number;
  /** Mana spent so far is implied: maximum Mana is derived from Intelligence. */
  currentMana: number;
  /** RULE DCC-FAVOR-001 - human crawlers start with 1, animals with 0. */
  aiFavor: number;
  /** RULE DCC-SIZE-001 - Table 10. Humans are Medium (4); animals are 2 to 4. */
  size: number;
  /**
   * Rounds left before the crawler dies, or null when not Dying.
   * RULE DCC-HP-005 - Rules for Survival p.42.
   */
  dyingRoundsRemaining: number | null;
  origin: CharacterOrigin | null;
  /**
   * Skill IDs currently carrying an advancement mark.
   * RULE DCC-ADV-SKILL-001 - C&C p.42. Marks persist on the character, not the
   * session: they survive closing the application and are erased only by the
   * advancement checkpoint that resolves them.
   */
  advancementMarks: string[];
  /**
   * The most recent changes made to this crawler, newest last, capped at
   * CHANGE_LOG_LIMIT. Every system that modifies the sheet writes here, so an
   * edit can be reviewed and reversed. Not a rules concept: an audit trail.
   */
  /**
   * What the crawler owns. RULE DCC-INV-002 - only the equipped ones do
   * anything; see src/rules/inventory.ts.
   */
  inventory: InventoryEntry[];
  /**
   * The ten quick-access slots. RULE DCC-HOT-001 - Rules p.48, C&C p.15.
   *
   * ALWAYS exactly `HOTLIST_SLOTS` long, with `null` for an empty slot. The
   * length is the capacity rule: there is no eleventh slot to write into, so
   * the limit cannot be exceeded by an import, an edit or a direct service
   * call rather than only by a check somebody might forget.
   *
   * Entries are references and carry NO mechanics - C&C p.15: "Items in
   * Hotlists do not grant you any added benefits: they're just easily
   * accessible." See `src/rules/hotlist.ts`.
   */
  hotlist: Hotlist;
  /** Bonded/owned animal companions linked to this crawler. Pets are not crawlers. */
  companions: PetCompanion[];
  changeLog: AppliedChange[];
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/** The crawler's entry for an item, or undefined when they have none. */
export function findInventoryEntry(
  character: Character,
  itemId: string,
): InventoryEntry | undefined {
  return character.inventory.find((e) => e.itemId === itemId);
}

export function findSkillRank(character: Character, skillId: string): number {
  const entry = character.skills.find((s) => s.skillId === skillId);
  return entry ? entry.rank : 0;
}
